"use client";

import { useEffect, useRef } from "react";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { BriefType, ExploreBriefCard } from "../types";
import { type BriefSurface, useBriefTracking } from "./use-brief-analytics";

export const BADGE_CONFIG: Record<
  BriefType,
  { label: string; className: string }
> = {
  RELEASE: {
    label: "Release",
    className:
      "bg-[var(--explore-accent-yellow)] text-[var(--explore-on-accent-text)]",
  },
  THREAD: {
    label: "Thread",
    className: "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]",
  },
  TOOL: {
    label: "Tool",
    className:
      "bg-[var(--explore-accent-yellow)] text-[var(--explore-on-accent-text)]",
  },
  GUIDE: {
    label: "Guide",
    className: "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]",
  },
  TIP: {
    label: "Tip",
    className: "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]",
  },
  BENCHMARK: {
    label: "Benchmark",
    className: "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]",
  },
};

interface BriefCardProps {
  brief: ExploreBriefCard;
  variant?: "default" | "mobile-tile";
  onClick?: () => void;
  /** Which surface the card is rendered on — attached to every analytics event. */
  surface?: BriefSurface;
  /** Zero-based index within its surface list, for brief_position_in_list tracking. */
  positionInList?: number | null;
}

const VIEW_THRESHOLD = 0.5;

export function BriefCard({
  brief,
  variant = "default",
  onClick,
  surface = "explore_briefs_section",
  positionInList = null,
}: BriefCardProps) {
  const { reportType } = useExploreRoute();
  const { trackCardViewed, trackCardClicked } = useBriefTracking({
    brief,
    edition: reportType,
    surface,
    positionInList,
  });

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (hasViewedRef.current) {
      return;
    }
    const node = buttonRef.current;
    if (!node) {
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasViewedRef.current) {
          hasViewedRef.current = true;
          trackCardViewed();
          observer.disconnect();
        }
      },
      { threshold: VIEW_THRESHOLD }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [trackCardViewed]);

  const handleClick = () => {
    trackCardClicked();
    onClick?.();
  };

  const badge = BADGE_CONFIG[brief.briefType];

  if (variant === "mobile-tile") {
    return (
      <button
        className="flex cursor-pointer flex-col rounded-xl bg-[var(--explore-bg-card)] p-3.5 text-left shadow-[var(--explore-inline-shadow)] transition-all active:scale-[0.98]"
        onClick={handleClick}
        ref={buttonRef}
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
        type="button"
      >
        <div className="mb-2 flex items-center gap-1.5">
          <span
            className={`rounded-full px-2 py-0.5 font-bold text-[0.65rem] uppercase tracking-[0.05em] ${badge.className}`}
          >
            {badge.label}
          </span>
          {brief.categoryDisplayName && (
            <span className="truncate text-[0.65rem] text-[var(--explore-text-muted)]">
              {brief.categoryEmoji} {brief.categoryDisplayName}
            </span>
          )}
        </div>
        <p className="mb-1 font-medium text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.45]">
          {brief.title}
        </p>
        {brief.tldr && (
          <p className="mb-2.5 line-clamp-2 text-[0.78rem] text-[var(--explore-text-secondary)] leading-[1.4]">
            {brief.tldr}
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-1.5">
          {brief.tags.map((tag) => (
            <span
              className="rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.7rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
          <span className="ml-auto shrink-0 text-[0.65rem] text-[var(--explore-text-muted)]">
            {brief.timeAgo}
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      className="flex w-[240px] shrink-0 cursor-pointer flex-col rounded-xl bg-[var(--explore-bg-card)] p-3.5 text-left shadow-[var(--explore-card-shadow-lg)] transition-all hover:shadow-[var(--explore-card-shadow-lg-hover)] md:w-auto"
      onClick={handleClick}
      ref={buttonRef}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <span
          className={`rounded-full px-1.5 py-0.5 font-bold text-[0.55rem] uppercase tracking-[0.05em] ${badge.className}`}
        >
          {badge.label}
        </span>
        {brief.categoryDisplayName && (
          <span className="truncate text-[0.55rem] text-[var(--explore-text-muted)]">
            {brief.categoryEmoji} {brief.categoryDisplayName}
          </span>
        )}
      </div>

      <p className="mb-1 line-clamp-3 font-medium text-[0.8rem] text-[var(--explore-text-primary)] leading-[1.4]">
        {brief.title}
      </p>
      {brief.tldr && (
        <p className="mb-2.5 line-clamp-2 text-[0.68rem] text-[var(--explore-text-secondary)] leading-[1.35]">
          {brief.tldr}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-1">
        {brief.tags.map((tag) => (
          <span
            className="rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.65rem] text-[var(--explore-tag-text)]"
            key={tag.slug}
          >
            {tag.displayName}
          </span>
        ))}
        <span className="ml-auto shrink-0 text-[0.55rem] text-[var(--explore-text-muted)]">
          {brief.timeAgo}
        </span>
      </div>
    </button>
  );
}
